import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Button,
  Card,
  Col,
  Input,
  Modal,
  Row,
  Space,
  Statistic,
  Table,
  Tag,
  Typography,
  message,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import {
  BuildOutlined,
  DeleteOutlined,
  EditOutlined,
  EyeOutlined,
  PlusOutlined,
  SearchOutlined,
} from '@ant-design/icons';

import bomService, { type BomMasterDto } from '../../services/bom.service';
import { useCurrency } from '@/contexts/CurrencyContext';
import { formatNumber } from '@/utils/format';
import { AedDirhamSign } from '@/components/currency';

const { Title, Text } = Typography;
const { Search } = Input;

const BOMsPage: React.FC = () => {
  const { formatCurrency } = useCurrency();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchText, setSearchText] = useState('');

  const bomsQuery = useQuery({
    queryKey: ['manufacturing-boms'],
    queryFn: () => bomService.list(),
  });
  const boms = bomsQuery.data ?? [];

  const deleteMutation = useMutation({
    mutationFn: (id: string) => bomService.remove(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['manufacturing-boms'] });
    },
  });

  const handleDelete = (record: BomMasterDto) => {
    Modal.confirm({
      title: 'Delete this BOM?',
      content: `${record.bom_code} — ${record.bom_name}`,
      okType: 'danger',
      onOk: async () => {
        try {
          await deleteMutation.mutateAsync(record.id);
          message.success('BOM deleted');
        } catch (error: unknown) {
          const msg = error instanceof Error ? error.message : 'Delete failed';
          message.error(msg);
        }
      },
    });
  };

  const columns: ColumnsType<BomMasterDto> = [
    {
      title: 'BOM Code',
      dataIndex: 'bom_code',
      key: 'bom_code',
      sorter: (a, b) => a.bom_code.localeCompare(b.bom_code),
    },
    {
      title: 'BOM Name',
      dataIndex: 'bom_name',
      key: 'bom_name',
      render: (text, record) => (
        <Space direction="vertical" size={0}>
          <Text strong>{text}</Text>
          <Text type="secondary" style={{ fontSize: '12px' }}>
            {record.item_name}
          </Text>
        </Space>
      ),
    },
    {
      title: 'Quantity',
      key: 'quantity',
      render: (_, record) => `${record.quantity} ${record.uom}`,
    },
    {
      title: 'Components',
      dataIndex: 'items',
      key: 'items',
      render: (items: BomMasterDto['items']) => <Text>{items?.length ?? 0} items</Text>,
    },
    {
      title: 'Operations',
      key: 'operations',
      render: (_, record) => <Text>{record.operation_count ?? record.operations?.length ?? 0}</Text>,
    },
    {
      title: 'Total Cost',
      dataIndex: 'total_cost',
      key: 'total_cost',
      render: (cost) => formatCurrency(cost),
      sorter: (a, b) => a.total_cost - b.total_cost,
    },
    {
      title: 'Status',
      key: 'status',
      render: (_, record) => (
        <Space>
          {record.is_active ? <Tag color="green">Active</Tag> : <Tag color="red">Inactive</Tag>}
          {record.is_default && <Tag color="blue">Default</Tag>}
        </Space>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space>
          <Button type="link" icon={<EyeOutlined />} onClick={() => navigate(`/mfg/boms/${record.id}`)}>
            View
          </Button>
          <Button type="link" icon={<EditOutlined />} onClick={() => navigate(`/mfg/boms/${record.id}`)}>
            Edit
          </Button>
          <Button type="link" danger icon={<DeleteOutlined />} onClick={() => handleDelete(record)}>
            Delete
          </Button>
        </Space>
      ),
    },
  ];

  const filteredBOMs = useMemo(
    () =>
      boms.filter(
        (bom) =>
          bom.bom_name.toLowerCase().includes(searchText.toLowerCase()) ||
          bom.bom_code.toLowerCase().includes(searchText.toLowerCase())
      ),
    [boms, searchText]
  );

  const totalBoms = boms.length;
  const activeBoms = boms.filter((b) => b.is_active).length;
  const avgComponents =
    totalBoms > 0
      ? formatNumber(boms.reduce((sum, b) => sum + b.items.length, 0) / totalBoms, {
          minimumFractionDigits: 1,
          maximumFractionDigits: 1,
          useGrouping: false,
        })
      : '0';
  const avgCost = totalBoms > 0 ? boms.reduce((sum, b) => sum + b.total_cost, 0) / totalBoms : 0;

  return (
    <div style={{ padding: '24px' }}>
      <Row gutter={16} style={{ marginBottom: 24 }}>
        <Col span={24}>
          <Title level={2}>
            <BuildOutlined /> Bill of Materials (BOM)
          </Title>
        </Col>
      </Row>

      <Row gutter={16} style={{ marginBottom: 24 }}>
        <Col span={6}>
          <Card>
            <Statistic title="Total BOMs" value={totalBoms} />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic title="Active BOMs" value={activeBoms} valueStyle={{ color: '#3f8600' }} />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic title="Average Components" value={avgComponents} />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic title="Avg Cost" value={avgCost} prefix={<AedDirhamSign />} precision={2} />
          </Card>
        </Col>
      </Row>

      <Card>
        <Space style={{ marginBottom: 16, width: '100%', justifyContent: 'space-between' }}>
          <Search
            placeholder="Search BOMs..."
            allowClear
            style={{ width: 300 }}
            onChange={(e) => setSearchText(e.target.value)}
            prefix={<SearchOutlined />}
          />
          <Button type="primary" icon={<PlusOutlined />} onClick={() => navigate('/mfg/boms/new')}>
            Create BOM
          </Button>
        </Space>

        <Table
          columns={columns}
          dataSource={filteredBOMs}
          rowKey="id"
          pagination={{ defaultPageSize: 10, showSizeChanger: true, pageSizeOptions: ['10', '20', '50', '100'] }}
          loading={bomsQuery.isPending}
        />
      </Card>
    </div>
  );
};

export default BOMsPage;
